























































































































































import {
  defineComponent,
  ref,
  useRouter,
  useRoute,
  computed,
  watch,
  useFetch,
  onMounted,
  nextTick,
  onUnmounted,
  useContext,
} from '@nuxtjs/composition-api';
import { debounce, uniq } from 'lodash-es';
import ArticleHeader from '~/components/pages/Article/ArticleHeader.vue';
import TopCarouselSlider from '~/components/pages/Top/TopCarouselSlider.vue';
import AppArticleCardList from '~/components/partials/App/AppArticleCardList.vue';
import AppAvatar from '~/components/partials/App/AppAvatar.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import AppSortSelect from '~/components/partials/App/AppSortSelect.vue';
import ContentArticleSideMenu from '~/components/partials/Content/ContentArticleSideMenu.vue';
import ContentSearchInput from '~/components/partials/Content/ContentSearchInput.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import PopularArticles from '~/components/partials/SilverEgg/PopularArticles.vue';
import { useListFavorite } from '~/composables/contents/useListFavorite';
import { useSilverEggApi } from '~/composables/silverEgg/useSilverEggApi';
import { useEnotecaApp } from '~/composables/useEnotecaApp';
import { useKeepScroll } from '~/composables/useKeepScroll';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { useStore } from '~/composables/useStore';
import {
  ARTICLE_SORT_OPTIONS,
  FETCH_ARTICLES_TYPE,
  ARTICLES_STATUS,
  SPEC,
  SMARTPHONE,
  CATEGORY_ID,
  DISPLAYED_ARTICLES_NUM,
  REQUEST_NUM_ARTICLE_RANKING,
} from '~/constants';
import { url, contentsSearchParams, convertRouteToUrl } from '~/utils';
import { PageError } from '~/utils/error';
import scrollToTop from '~/utils/scrollToTop';
import { RecommendItem } from '~/types/common/silverEgg';
import type { ArticleCategory, EditorProfile, ArticleCard } from '~/types/modules/contents';
import type { TopSlider } from '~/types/modules/top';
import { ArticleType, FavoriteTypes } from '~/enums';

export default defineComponent({
  components: {
    AppArticleCardList,
    TopCarouselSlider,
    ContentArticleSideMenu,
    ContentLayout,
    ContentSearchInput,
    AppAvatar,
    AppSeeMoreButton,
    AppSortSelect,
    AppLoader,
    AppIcon,
    AppLink,
    ArticleHeader,
    AppImage,
    PopularArticles,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { scrollToPreviousPosition, setPageData } = useKeepScroll();
    const defaultSpImage = require('~/assets/svg/article-thumbnail-no-img-sp.svg');
    const defaultPcImage = require('~/assets/svg/article-thumbnail-no-img-pc.svg');
    const { isEnotecaAppWebView } = useEnotecaApp();
    const { i18n, app } = useContext();
    const { fetchAllFavorites } = useListFavorite();
    const { fetchRecommendItems } = useSilverEggApi();
    const { isLargeDesktop } = useMatchMedia(); // onMountedで使用するため問題なし

    // 入力中の文字
    const searchString = ref('');
    // 記事
    const articles = ref<ArticleCard[]>([]);
    // 記事の合計数
    const total = ref(0);
    // ページングのカウント
    const pagination = ref(store.getters['internals/keepScroll/pagination'](route.value.path, route.value.fullPath, 0));
    // もっと見るボタンのローディングフラグ
    const isLoadingMoreItems = ref(false);
    const categories = ref<ArticleCategory[]>([]);
    const authors = ref<EditorProfile[]>([]);
    const isLoading = ref(true);
    // 読み物トップスライダー
    const magazineTopSlider = ref<TopSlider[]>([]);
    // 読み物作成者情報
    const authorState = ref();
    // シルバーエッグ
    const popularArticles = ref<RecommendItem[] | undefined>([]);
    const rqid = ref('');
    const spec = ref(app.$ua.deviceType() === SMARTPHONE ? SPEC.ARTICLE_RANKING_TOP_SP : SPEC.ARTICLE_RANKING_TOP_PC);
    const rankingPagination = ref(
      store.getters['internals/keepScroll/pagination'](route.value.path, route.value.fullPath, 1)
    );

    const { fetchState } = useFetch(async () => {
      try {
        isLoading.value = true;
        categories.value = await store.dispatch('modules/content/fetchArticleCategories', {});

        if (status.value === ARTICLES_STATUS.CATEGORY && !categoryState.value) throw new PageError(404, {});

        const { permalink } = route.value.params;

        if (status.value === ARTICLES_STATUS.AUTHOR) {
          authors.value = await store.dispatch('modules/content/fetchAuthors', {
            permalinks: [permalink],
          });

          if (!authors.value.length) throw new PageError(404, {});

          const foundAuthor = authors.value.find((author) => author.permalink === permalink);
          authorState.value = {
            permalink,
            id: foundAuthor?.id,
            name: foundAuthor?.name,
            image: foundAuthor?.image,
            profile: foundAuthor?.profile,
          };
        }

        // useFetchの際にはisLoadingをfalseにしないようにする
        await fetchArticles(FETCH_ARTICLES_TYPE.INITIAL, true);

        // 読み物トップページのとき、読み物トップスライダーのパーツを取得する
        if (Object.keys(route.value.params).length === 0) {
          const { result } = await store.dispatch('modules/content/fetchMagazineSlider', {});
          if (result) {
            magazineTopSlider.value = result.map(
              (item: any): TopSlider => ({
                id: item.article_id,
                title: item.title,
                pc_img: item.contents.main_image_pc,
                sp_img: item.contents.main_image_sp,
                url: item.contents.url,
              })
            );
          }
        }

        if (title.value) {
          searchString.value = title.value;
        }
      } finally {
        isLoading.value = false;
        nextTick(() => scrollToPreviousPosition());
      }
    });

    // バナー下のバナータイトルを更新する
    const bannerIndex = ref(0);
    const setActiveBannerIndex = (index: number) => {
      bannerIndex.value = index;
    };

    onMounted(async () => {
      // ヘッダーメニューのバッジ用に閲覧時刻を記録
      store.commit('internals/header/setArticlesReadAt');

      fetchAllFavorites(FavoriteTypes.ARTICLE);

      // 記事ランキングを取得
      const res = await fetchRecommendItems({
        spec: spec.value,
        num: REQUEST_NUM_ARTICLE_RANKING,
        cat: CATEGORY_ID.ARTICLE,
      });
      popularArticles.value = res.items;
      rqid.value = res.rqid;
      // サイドメニュー(xlが分岐点)に表示するときのみ、SPサムネ画像に差し替え
      if (isLargeDesktop.value && popularArticles.value?.length) {
        const images = await store.dispatch('modules/content/fetchArticleSpThumbnails', {
          ids: popularArticles.value.map((item) => item._id),
        });
        popularArticles.value = popularArticles.value.map((item) => {
          const img = images.find((image) => image.id === item._id)?.thumbnailImageSp ?? item.img_url;
          return {
            ...item,
            img_url: img,
          };
        });
      }
    });

    onUnmounted(() => {
      setPageData({
        pagination: [pagination.value, rankingPagination.value], // 0:読み物一覧, 1:読み物ランキング
        detailsElements: [{ elementId: 'article-related-links' }], // 0:関連リンク
      });
    });

    // 画面の状態
    const status = computed(() => {
      const { name } = route.value;

      if (!name) return;

      const path = name.split('-')[2];

      return path;
    });

    // 選択中のカテゴリ
    const categoryState = computed(() => {
      if (status.value !== ARTICLES_STATUS.CATEGORY || !categories.value.length) {
        return;
      }

      const { permalink } = route.value.params;

      const category = categories.value.find((category: ArticleCategory) => category.permalink === permalink);

      if (!category) return;

      return {
        permalink,
        id: category.id,
        name: category.name,
        description: category.description,
      };
    });

    // 検索中の文字列
    const title = computed(() => {
      const { _s } = route.value.query;

      return typeof _s === 'string' && _s ? _s : undefined;
    });

    // 検索中の文字
    const searchingWords = computed(() => {
      if (title.value) return i18n.t('Content-01.searchTitle', { searchTitle: title.value }).toString();

      if (authorState.value) {
        return authorState.value.name || '';
      }

      return (
        categories.value.find(({ permalink }) => permalink && permalink === categoryState.value?.permalink)?.name || ''
      );
    });

    // 表示タイトル
    const displayedTitle = computed(() => {
      return searchingWords.value || i18n.t('Content-04.pageTitle').toString();
    });

    const description = computed(() => {
      return categoryState.value?.description || i18n.t('Content-04.description').toString();
    });

    // もっと見るボタンの表示フラグ
    const showSeeMoreButton = computed(() => {
      return articles.value.length < total.value;
    });

    // 選択中のソートの順番
    const currentSortIndex = computed({
      get(): number {
        const { sortIndex } = route.value.query;

        return typeof sortIndex === 'string' ? +sortIndex : 0;
      },
      async set(sortIndex: number) {
        isLoading.value = true;
        clearState();

        await router.push({
          query: {
            ...route.value.query,
            _s: title.value,
            sortIndex: sortIndex ? sortIndex.toString() : undefined,
          },
        });
      },
    });

    watch(
      [title],
      debounce(async () => {
        searchString.value = title.value || '';
        pagination.value = 1;
        await fetchArticles(FETCH_ARTICLES_TYPE.INITIAL, false);
      }, 100)
    );

    watch(
      [pagination],
      debounce(async () => {
        if (pagination.value > 1) {
          await fetchArticles(FETCH_ARTICLES_TYPE.NORMAL, false);
        }
      }, 100)
    );

    watch(
      [currentSortIndex],
      debounce(async () => {
        await fetchArticles(FETCH_ARTICLES_TYPE.INITIAL, false);
      }, 100)
    );

    /**
     * API連携
     */
    const fetchArticles = async (type: string, isFetch: boolean) => {
      try {
        if (pagination.value <= 1) isLoading.value = true;

        isLoadingMoreItems.value = true;

        const { q } = contentsSearchParams({
          articleType: ArticleType.Articles,
          title: title.value,
          categoryId: categoryState.value?.id.toString(),
          authorId: authorState.value?.id?.toString(),
        });

        if (type === FETCH_ARTICLES_TYPE.INITIAL) {
          const result = await store.dispatch('modules/content/fetchArticles', {
            limit: pagination.value * DISPLAYED_ARTICLES_NUM,
            q,
            sortIndex: currentSortIndex.value,
          });
          articles.value = result.articles;
          total.value = result.total;
        } else {
          const result = await store.dispatch('modules/content/fetchArticles', {
            limit: DISPLAYED_ARTICLES_NUM,
            offset: (pagination.value - 1) * DISPLAYED_ARTICLES_NUM,
            q,
            sortIndex: currentSortIndex.value,
          });
          articles.value.push(...result.articles);
          total.value = result.total;
        }

        const authorIds = articles.value
          .filter((article) => article.authorId)
          .map((article) => Number(article.authorId));

        const filteredAuthorIds = uniq(authorIds);

        if (status.value !== ARTICLES_STATUS.AUTHOR && authorIds.length > 0) {
          authors.value = await store.dispatch('modules/content/fetchAuthors', {
            authorIds: filteredAuthorIds,
          });
        }
      } finally {
        // useFetchの際にはisLoadingをfalseにしないようにする
        isLoading.value = isFetch;
        isLoadingMoreItems.value = false;
      }
    };

    /**
     * もっと見るボタン
     */
    const fetchMoreItems = async () => {
      pagination.value += 1;
    };

    /**
     * フリーワードで検索
     */
    const searchWords = async () => {
      // // 同じURLになる場合、実行しない
      if (!searchString.value || searchString.value === title.value) {
        return;
      }

      const query: { _s: string; sortIndex?: string } = {
        _s: searchString.value,
      };

      if (currentSortIndex.value) {
        query.sortIndex = currentSortIndex.value.toString();
      }

      const requestUrl = convertRouteToUrl({
        path: url('ARTICLE_LIST'),
        query,
      });

      if (isSimpleLayout.value) {
        const searchLink = document.createElement('a');
        searchLink.href = requestUrl;
        document.body.appendChild(searchLink);

        // ネイティブ画面は初期化する
        searchString.value = '';
        (document.activeElement as HTMLInputElement).blur();

        searchLink.click();
        document.body.removeChild(searchLink);

        return;
      }

      isLoading.value = true;
      clearState();

      await router.push(requestUrl);
      scrollToTop();
    };

    /**
     * キーワードをクリア
     */
    const clearWords = async () => {
      // クエリがない（検索を走らせていない）場合、実行しない
      if (!route.value.query._s) {
        return;
      }

      isLoading.value = true;
      searchString.value = '';

      clearState();

      await router.push({
        query: {},
      });
    };

    /** 読み物一覧がデフォルトの一覧かどうか */
    const isDefaultList = computed(
      () => route.value.path === url('ARTICLE_LIST') && route.value.query._s === undefined
    );

    /** アプリで読み物TOPのとき、ArticleHeaderのみ表示する */
    const isSimpleLayout = computed(() => isEnotecaAppWebView.value && route.value.path === url('ARTICLE_LIST'));

    /**
     * 状態をクリアする
     */
    const clearState = () => {
      articles.value = [];
      pagination.value = 1;
    };

    // パンくず
    const breadcrumbs = computed(() => {
      const category =
        categories.value.find(({ permalink }) => permalink && permalink === categoryState.value?.permalink)?.name || '';
      if (category) {
        return [
          { title: i18n.t('Content-04.breadcrumbs.title').toString(), to: url('ARTICLE_LIST') },
          { title: category, to: null },
        ];
      }

      if (searchingWords.value) {
        return [
          { title: i18n.t('Content-04.breadcrumbs.title').toString(), to: url('ARTICLE_LIST') },
          { title: searchingWords.value, to: null },
        ];
      }

      return [{ title: i18n.t('Content-04.breadcrumbs.title').toString(), to: null }];
    });

    /**
     * ContentsFilterModalで利用できる型に合わせて読み物カテゴリ情報を整形する
     */
    const sortedCategories = computed(() => {
      const all = {
        label: i18n.t('Content-04.contentsModal.label').toString(),
        id: '',
        children: undefined,
      };
      const otherCategories = categories.value.map((category) => {
        return {
          label: category.name,
          id: category.permalink,
          children: undefined,
        };
      });
      return [all, ...otherCategories];
    });

    useLayoutProps(() => ({
      fetchState,
      layout: {
        header: isSimpleLayout.value ? 'never' : 'always',
        headerMenu: 'never', // ContentLayoutの中で設置
        footer: isSimpleLayout.value ? 'never' : 'always',
        footerMenu: isSimpleLayout.value ? 'never' : 'always',
      },
      head: {
        titleTemplate: null,
        title: i18n.t('Content-04.seoTitle', { seoTitle: displayedTitle.value }).toString(),
        description: description.value,
      },
      og: {
        image: authorState.value ? null : url('ARTICLE_IMAGE_PATH'),
      },
      breadcrumbs: isSimpleLayout.value ? [] : breadcrumbs.value,
    }));

    return {
      searchString,
      searchWords,
      total,
      fetchMoreItems,
      showSeeMoreButton,
      url,
      clearWords,
      ARTICLE_SORT_OPTIONS,
      currentSortIndex,
      articles,
      authorState,
      authors,
      categories,
      categoryState,
      defaultSpImage,
      defaultPcImage,
      isLoading,
      isLoadingMoreItems,
      sortedCategories,
      magazineTopSlider,
      bannerIndex,
      setActiveBannerIndex,
      title,
      displayedTitle,
      popularArticles,
      spec,
      rqid,
      rankingPagination,
      isDefaultList,
      isSimpleLayout,
    };
  },
  head: {},
});
